import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { 
  LogOut, CreditCard, ArrowRight, ArrowLeft, Info, Check, 
  AlertCircle, ChevronRight, RotateCcw, Save, Upload, FileText, X, CheckCircle
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent } from "../../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { useAuth, API } from "../../App";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import DisclaimerGate from "../../components/DisclaimerGate";
import { INITIAL_QUESTIONNAIRE } from "../../components/dashboard";

// Mobile keeps two extra fields on the questionnaire object itself
// (acuteSymptoms + complaints). The rest is the shared clinical schema.
const MOBILE_INITIAL_QUESTIONNAIRE = {
  ...INITIAL_QUESTIONNAIRE,
  acuteSymptoms: "",
  complaints: "",
};

// Storage key for saving progress
const PROGRESS_STORAGE_KEY = "thyroid_mobile_progress";

// Thyroid tests data
const THYROID_TESTS = [
  { key: "tsh", name: "TSH", unit: "mIU/L", required: true },
  { key: "ft4", name: "fT4", unit: "pmol/L" },
  { key: "ft3", name: "fT3", unit: "pmol/L" },
  { key: "atpo", name: "aTPO", unit: "kU/L" },
  { key: "tsi", name: "TSI", unit: "IU/L" },
  { key: "tg", name: "TG", unit: "mcg/L" },
  { key: "atg", name: "aTG", unit: "kU/L" },
  { key: "calcitonin", name: "Kaltsitoniin", unit: "pmol/L" }
];

// 6-Step indicator component (extracted to avoid recreating on every parent render)
const StepIndicator = ({ step, t }) => (
  <div className="flex items-center justify-center gap-0 py-4 px-2 overflow-x-auto" style={{ background: "#f7f9fe" }}>
    {[1, 2, 3, 4, 5, 6].map((s) => (
      <React.Fragment key={s}>
        <div className="flex flex-col items-center min-w-[42px]">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
            step === s ? 'text-white' :
            step > s ? 'text-white' : ''
          }`} style={
            step === s ? { background: "linear-gradient(180deg, #00aeef 0%, #00658d 100%)", boxShadow: "0 4px 12px rgba(0,101,141,0.3)" } :
            step > s ? { background: "#00658D" } :
            { background: "#ebeef3", color: "#6e7881" }
          }>
            {step > s ? <Check className="w-4 h-4" /> : s}
          </div>
          <span className={`text-[9px] mt-1.5 font-semibold tracking-wider ${
            step === s ? 'font-bold' : ''
          }`} style={{ color: step >= s ? "#00658D" : "#94a3b8" }}>
            {t(`dashboard.step${s}`)}
          </span>
        </div>
        {s < 6 && <div className="w-4 h-[2px] mt-[-12px] flex-shrink-0" style={{ background: step > s ? "#00658D" : "#dfe3e8" }} />}
      </React.Fragment>
    ))}
  </div>
);

// Mobile Yes/No Toggle - clinical design
const MobileYesNo = ({ id, value, onChange, label }) => {
  const { t } = useTranslation();
  return (
    <div className="py-4">
      <Label className="font-medium text-sm block mb-3" style={{ color: "#181c20" }}>{label}</Label>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onChange("yes")}
          className={`flex-1 py-2.5 rounded-full text-sm font-semibold transition-all ${
            value === "yes" ? "text-white" : ""
          }`}
          style={value === "yes" 
            ? { background: "linear-gradient(180deg, #00aeef 0%, #00658d 100%)", boxShadow: "0 4px 12px rgba(0,101,141,0.2)" }
            : { background: "#ebeef3", color: "#6e7881" }
          }
          data-testid={`${id}-yes`}
        >
          {t('common.yes')}
        </button>
        <button
          type="button"
          onClick={() => onChange("no")}
          className={`flex-1 py-2.5 rounded-full text-sm font-semibold transition-all ${
            value === "no" ? "text-white" : ""
          }`}
          style={value === "no" 
            ? { background: "#3e4850" }
            : { background: "#ebeef3", color: "#6e7881" }
          }
          data-testid={`${id}-no`}
        >
          {t('common.no')}
        </button>
      </div>
    </div>
  );
};

export default function MobileDashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const startingNew = searchParams.get("new") === "1";
  const { t } = useTranslation();
  const { user, token, logout } = useAuth();
  const [step, setStep] = useState(1); // 1=symptoms, 2=questionnaire, 3=lab results, 4=ultrasound, 5=payment (Stripe), 6=analysis (Results page)
  const [testValues, setTestValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [complaints, setComplaints] = useState("");
  const [ultrasoundText, setUltrasoundText] = useState("");
  const [ultrasoundUploading, setUltrasoundUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [extractedNotice, setExtractedNotice] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, [step]);
  const [expandedTest, setExpandedTest] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [checkingExisting, setCheckingExisting] = useState(true);

  // On mount, check whether the patient already has a diagnosis in progress
  // (paid / analyzing / analyzed / approved / error). If so, take them
  // straight to /results/:id instead of showing an empty step-1 form.
  useEffect(() => {
    if (!token) return;
    if (startingNew) { setCheckingExisting(false); return; }
    if (user?.is_doctor) { setCheckingExisting(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/diagnoses`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const active = (res.data || [])
          .filter(d => ["paid", "analyzing", "analyzed", "approved", "error"].includes(d.status))
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
        if (active) {
          navigate(`/results/${active.diagnosis_id}`, { replace: true });
          return;
        }
      } catch (_) { /* let the user proceed to the form */ }
      if (!cancelled) setCheckingExisting(false);
    })();
    return () => { cancelled = true; };
  }, [token, user?.is_doctor, navigate, startingNew]);

  // Questionnaire state — sourced from the shared clinical schema so both
  // desktop and mobile stay in lockstep whenever new medical fields are added.
  const [questionnaire, setQuestionnaire] = useState(MOBILE_INITIAL_QUESTIONNAIRE);

  // Load saved progress on mount. We use setState inside useEffect (not useState
  // lazy init) because we need access to the auth context user.user_id which is
  // only stable AFTER auth has hydrated. This effect runs once per user change.
  useEffect(() => {
    const userId = user?.user_id || user?.id;
    if (!userId) return;

    try {
      const savedData = localStorage.getItem(`${PROGRESS_STORAGE_KEY}_${userId}`);
      if (savedData) {
        const parsed = JSON.parse(savedData);
        // Check if data is less than 24 hours old
        const savedTime = new Date(parsed.savedAt);
        const now = new Date();
        const hoursDiff = (now - savedTime) / (1000 * 60 * 60);

        if (hoursDiff < 24) {
          setStep(parsed.step || 1);
          setComplaints(parsed.complaints || "");
          setQuestionnaire(prev => ({ ...prev, ...parsed.questionnaire }));
          setTestValues(parsed.testValues || {});
          setUltrasoundText(parsed.ultrasoundText || "");
          setLastSaved(savedTime);
        } else {
          // Clear old data
          localStorage.removeItem(`${PROGRESS_STORAGE_KEY}_${userId}`);
        }
      }
    } catch (e) {
      console.error("Failed to restore progress:", e);
    }
  }, [user?.user_id, user?.id]);

  // Save progress whenever data changes
  const saveProgress = useCallback(() => {
    const userId = user?.user_id || user?.id;
    if (!userId) return;
    
    const dataToSave = {
      step,
      complaints,
      questionnaire,
      testValues,
      ultrasoundText,
      savedAt: new Date().toISOString()
    };
    
    try {
      localStorage.setItem(`${PROGRESS_STORAGE_KEY}_${userId}`, JSON.stringify(dataToSave));
      setLastSaved(new Date());
    } catch (e) {
      console.error("Failed to save progress:", e);
    }
  }, [user?.user_id, user?.id, step, complaints, questionnaire, testValues, ultrasoundText]);

  // Auto-save when data changes (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      saveProgress();
    }, 1000); // Save 1 second after last change
    
    return () => clearTimeout(timer);
  }, [saveProgress]);

  // Clear saved progress
  const clearProgress = () => {
    const userId = user?.user_id || user?.id;
    if (!userId) return;
    localStorage.removeItem(`${PROGRESS_STORAGE_KEY}_${userId}`);
    setStep(1);
    setComplaints("");
    setQuestionnaire(MOBILE_INITIAL_QUESTIONNAIRE);
    setTestValues({});
    setUltrasoundText("");
    setUploadedFile(null);
    setExtractedNotice(false);
    setLastSaved(null);
  };

  const handleTestChange = (key, value) => {
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setTestValues({ ...testValues, [key]: value });
    }
  };

  const handleQuestionnaireChange = (key, value) => {
    setQuestionnaire({ ...questionnaire, [key]: value });
  };

  const canProceedFromSymptoms = () => {
    return true; // Symptoms are optional
  };

  const canProceedFromQuestionnaire = () => {
    return questionnaire.age && questionnaire.gender;
  };

  const canProceedFromLabResults = () => {
    return testValues.tsh && testValues.tsh !== "";
  };

  const handleSubmit = async () => {
    if (!canProceedFromLabResults()) {
      setError(t('dashboard.tshRequired') || 'TSH value is required');
      return;
    }

    setLoading(true);
    setError("");
    try {
      // Convert test values to numbers
      const numericTests = {};
      Object.keys(testValues).forEach(key => {
        if (testValues[key]) {
          numericTests[key] = parseFloat(testValues[key]);
        }
      });

      const response = await axios.post(`${API}/diagnosis`, {
        tests: numericTests,
        questionnaire: {
          ...questionnaire,
          complaints: complaints
        },
        ultrasound_text: ultrasoundText.trim() || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const diagnosisId = response.data.diagnosis_id;
      
      // Clear saved progress after successful submission
      const userId = user?.user_id || user?.id;
      if (userId) {
        localStorage.removeItem(`${PROGRESS_STORAGE_KEY}_${userId}`);
      }
      
      // If diagnosis status is "paid" (doctor/subscription), go directly to analysis
      if (response.data.status === "paid") {
        navigate(`/results/${diagnosisId}`);
      } else {
        // Patient needs to pay - create checkout (Step 4: Payment)
        const checkoutResponse = await axios.post(`${API}/payment/checkout`, {
          diagnosis_id: diagnosisId,
          origin_url: window.location.origin
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Redirect to Stripe (external payment page)
        window.location.href = checkoutResponse.data.url;
      }
    } catch (err) {
      setError(err.response?.data?.detail || t('common.error'));
    }
    setLoading(false);
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleDisclaimerDecline = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ fontFamily: "'Inter', sans-serif", background: "#f7f9fe", color: "#181c20" }}>
      {checkingExisting && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center" style={{ background: "#f7f9fe" }}>
          <div className="flex items-center gap-3 text-sm" style={{ color: "#00658D" }}>
            <div className="w-5 h-5 rounded-full border-2 border-slate-200 border-t-[#00658D] animate-spin" />
            <span>{t('common.loading') || 'Laen...'}</span>
          </div>
        </div>
      )}
      {!disclaimerAccepted && (
        <DisclaimerGate
          onAccept={() => setDisclaimerAccepted(true)}
          onDecline={handleDisclaimerDecline}
        />
      )}
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl" style={{ background: "rgba(255,255,255,0.9)", boxShadow: "0 1px 12px rgba(0,101,141,0.05)" }}>
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center overflow-hidden" style={{ background: "#ffffff", border: "1px solid #e2e8f0" }}>
              <img src="/brand/logo.webp" alt="Kilpnäärme Kliinik" className="w-9 h-9 object-contain" />
            </div>
            <div>
              <p className="text-sm font-bold truncate max-w-32" style={{ color: "#181c20" }}>{user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {(step > 1 || complaints || Object.keys(testValues).length > 0) && (
              <button
                onClick={clearProgress}
                className="p-2 transition-colors"
                style={{ color: "#94a3b8" }}
                title={t('dashboard.startOver') || 'Start Over'}
                data-testid="mobile-start-over-btn"
              >
                <RotateCcw className="w-5 h-5" />
              </button>
            )}
            <LanguageSwitcher />
            <button
              onClick={handleLogout}
              className="p-2 transition-colors"
              style={{ color: "#6e7881" }}
              data-testid="mobile-logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
        <StepIndicator step={step} t={t} />
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto pb-24">

        {/* Auto-save indicator */}
        {lastSaved && (
          <div className="mx-4 mt-2 flex items-center gap-1.5 text-xs" style={{ color: "#94a3b8" }}>
            <Save className="w-3 h-3" />
            <span>{t('dashboard.autoSaved') || 'Auto-saved'}</span>
          </div>
        )}

        {error && (
          <div className="mx-4 mt-4 p-3 rounded-xl text-sm flex items-start gap-2" style={{ background: "rgba(141,79,0,0.08)", color: "#6d3a00" }}>
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            {error}
          </div>
        )}

        {/* Step 1: Symptoms */}
        {step === 1 && (
          <div className="p-4">
            <div className="mb-6">
              <h1 className="text-xl font-bold mb-2" style={{ color: "#181c20" }}>{t('dashboard.complaintsTitle')}</h1>
              <p className="text-sm" style={{ color: "#3e4850" }}>{t('dashboard.complaintsDesc')}</p>
            </div>

            <div className="rounded-2xl mb-4 overflow-hidden" style={{ background: "#fff", boxShadow: "0 4px 20px rgba(0,101,141,0.06)" }}>
              <div className="p-5">
                <Label className="font-medium text-sm block mb-3" style={{ color: "#181c20" }}>
                  {t('dashboard.complaintsLabel')}
                </Label>
                <textarea
                  value={complaints}
                  onChange={(e) => setComplaints(e.target.value)}
                  className="w-full h-32 p-3 rounded-xl resize-none text-base transition-all focus:outline-none focus:ring-2"
                  style={{ background: "#ebeef3", border: "1px solid rgba(189,200,209,0.15)", "--tw-ring-color": "#00658D" }}
                  placeholder={t('dashboard.complaintsPlaceholder')}
                  data-testid="mobile-complaints-input"
                  onFocus={e => e.target.style.borderColor = "#00658D"}
                  onBlur={e => e.target.style.borderColor = "rgba(189,200,209,0.15)"}
                />
                <p className="text-xs mt-2 italic" style={{ color: "#94a3b8" }}>
                  {t('dashboard.complaintsOptional')}
                </p>
              </div>
            </div>

            {/* Acute Symptoms */}
            <div className="rounded-2xl overflow-hidden" style={{ background: "#fff", boxShadow: "0 4px 20px rgba(0,101,141,0.06)" }}>
              <div className="p-5">
                <h3 className="font-bold mb-1" style={{ color: "#00658D" }}>{t('dashboard.acuteSymptoms')}</h3>
                <MobileYesNo
                  id="hasFever"
                  value={questionnaire.hasFever}
                  onChange={(v) => handleQuestionnaireChange("hasFever", v)}
                  label={t('dashboard.hasFever')}
                />
                <MobileYesNo
                  id="hasFatigue"
                  value={questionnaire.hasFatigue}
                  onChange={(v) => handleQuestionnaireChange("hasFatigue", v)}
                  label={t('dashboard.hasFatigue')}
                />
                <MobileYesNo
                  id="hasNeckPain"
                  value={questionnaire.hasNeckPain}
                  onChange={(v) => handleQuestionnaireChange("hasNeckPain", v)}
                  label={t('dashboard.hasNeckPain')}
                />
                <MobileYesNo
                  id="symptomsAfterVirusInfection"
                  value={questionnaire.symptomsAfterVirusInfection}
                  onChange={(v) => handleQuestionnaireChange("symptomsAfterVirusInfection", v)}
                  label={t('dashboard.symptomsAfterVirus')}
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Questionnaire */}
        {step === 2 && (
          <div className="p-4">
            <div className="mb-6">
              <h1 className="text-xl font-bold mb-2" style={{ color: "#181c20" }}>{t('dashboard.healthHistory')}</h1>
              <p className="text-sm" style={{ color: "#3e4850" }}>{t('dashboard.healthHistoryDesc')}</p>
            </div>

            {/* Basic Info */}
            <div className="rounded-2xl mb-4 overflow-hidden" style={{ background: "#fff", boxShadow: "0 4px 20px rgba(0,101,141,0.06)" }}>
              <div className="p-5">
                <h3 className="font-bold mb-3" style={{ color: "#00658D" }}>{t('dashboard.basicInfo')}</h3>
                
                {/* Age */}
                <div className="py-5">
                  <Label className="font-medium text-sm block mb-2" style={{ color: "#181c20" }}>{t('dashboard.age')} *</Label>
                  <Input
                    type="number"
                    value={questionnaire.age}
                    onChange={(e) => handleQuestionnaireChange("age", e.target.value)}
                    className="h-11 text-base rounded-xl"
                    placeholder={t('dashboard.enterAge')}
                    data-testid="mobile-age-input"
                  />
                </div>

                {/* Gender */}
                <div className="py-5">
                  <Label className="font-medium text-sm block mb-2" style={{ color: "#181c20" }}>{t('dashboard.gender')} *</Label>
                  <Select
                    value={questionnaire.gender}
                    onValueChange={(value) => handleQuestionnaireChange("gender", value)}
                  >
                    <SelectTrigger className="h-11 text-base rounded-xl" data-testid="mobile-gender-select">
                      <SelectValue placeholder={t('dashboard.selectGender')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">{t('dashboard.male')}</SelectItem>
                      <SelectItem value="female">{t('dashboard.female')}</SelectItem>
                      <SelectItem value="other">{t('dashboard.other')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Pregnancy (only for female) */}
                {questionnaire.gender === "female" && (
                  <MobileYesNo
                    id="pregnant"
                    value={questionnaire.pregnant}
                    onChange={(v) => handleQuestionnaireChange("pregnant", v)}
                    label={t('dashboard.pregnant')}
                  />
                )}
              </div>
            </div>

            {/* Thyroid History */}
            <div className="rounded-2xl mb-4 overflow-hidden" style={{ background: "#fff", boxShadow: "0 4px 20px rgba(0,101,141,0.06)" }}>
              <div className="p-5">
                <h3 className="font-bold mb-3" style={{ color: "#00658D" }}>{t('dashboard.healthHistory')}</h3>
                
                <MobileYesNo
                  id="thyroidDiseasesBefore"
                  value={questionnaire.thyroidDiseasesBefore}
                  onChange={(v) => handleQuestionnaireChange("thyroidDiseasesBefore", v)}
                  label={t('dashboard.thyroidDiseases')}
                />

                {questionnaire.thyroidDiseasesBefore === "yes" && (
                  <div className="pl-3 border-l-2 mt-2" style={{ borderColor: "#b9e2ff" }}>
                    <MobileYesNo
                      id="graves"
                      value={questionnaire.graves}
                      onChange={(v) => handleQuestionnaireChange("graves", v)}
                      label={t('dashboard.graves')}
                    />
                    <MobileYesNo
                      id="hashimoto"
                      value={questionnaire.hashimoto}
                      onChange={(v) => handleQuestionnaireChange("hashimoto", v)}
                      label={t('dashboard.hashimoto')}
                    />
                    <MobileYesNo
                      id="thyroidNodules"
                      value={questionnaire.thyroidNodules}
                      onChange={(v) => handleQuestionnaireChange("thyroidNodules", v)}
                      label={t('dashboard.thyroidNodules')}
                    />
                    <MobileYesNo
                      id="thyroidCancer"
                      value={questionnaire.thyroidCancer}
                      onChange={(v) => handleQuestionnaireChange("thyroidCancer", v)}
                      label={t('dashboard.thyroidCancer')}
                    />
                    <MobileYesNo
                      id="totalThyroidectomy"
                      value={questionnaire.totalThyroidectomy}
                      onChange={(v) => handleQuestionnaireChange("totalThyroidectomy", v)}
                      label={t('dashboard.totalThyroidectomy')}
                    />
                    <MobileYesNo
                      id="partialThyroidectomy"
                      value={questionnaire.partialThyroidectomy}
                      onChange={(v) => handleQuestionnaireChange("partialThyroidectomy", v)}
                      label={t('dashboard.partialThyroidectomy')}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Medications */}
            <div className="rounded-2xl mb-4 overflow-hidden" style={{ background: "#fff", boxShadow: "0 4px 20px rgba(0,101,141,0.06)" }}>
              <div className="p-5">
                <h3 className="font-bold mb-3" style={{ color: "#00658D" }}>{t('dashboard.medications')}</h3>
                
                <MobileYesNo
                  id="levothyroxin"
                  value={questionnaire.levothyroxin}
                  onChange={(v) => handleQuestionnaireChange("levothyroxin", v)}
                  label={t('dashboard.levothyroxin')}
                />

                {questionnaire.levothyroxin === "yes" && (
                  <div className="pl-3 border-l-2 py-2 space-y-2" style={{ borderColor: "#b9e2ff" }}>
                    <Label className="font-medium text-sm block mb-2" style={{ color: "#3e4850" }}>{t('dashboard.levothyroxinDosage')}</Label>
                    <Input
                      type="text"
                      value={questionnaire.levothyroxinDosage}
                      onChange={(e) => handleQuestionnaireChange("levothyroxinDosage", e.target.value)}
                      className="h-11 text-base rounded-xl"
                      placeholder="e.g., 50 mcg"
                      data-testid="mobile-levo-dosage"
                    />
                    <MobileYesNo
                      id="levoBeforeBreakfast"
                      value={questionnaire.levoBeforeBreakfast}
                      onChange={(v) => handleQuestionnaireChange("levoBeforeBreakfast", v)}
                      label={t('dashboard.levoBeforeBreakfast')}
                    />
                    <MobileYesNo
                      id="levoRegularly"
                      value={questionnaire.levoRegularly}
                      onChange={(v) => handleQuestionnaireChange("levoRegularly", v)}
                      label={t('dashboard.levoRegularly')}
                    />
                    <div className="pt-1">
                      <p className="text-xs font-medium mb-1" style={{ color: "#6e7881" }}>{t('dashboard.interferingMeds')}</p>
                    </div>
                    <MobileYesNo
                      id="takesCalcium"
                      value={questionnaire.takesCalcium}
                      onChange={(v) => handleQuestionnaireChange("takesCalcium", v)}
                      label={t('dashboard.calcium')}
                    />
                    <MobileYesNo
                      id="takesIron"
                      value={questionnaire.takesIron}
                      onChange={(v) => handleQuestionnaireChange("takesIron", v)}
                      label={t('dashboard.iron')}
                    />
                    <MobileYesNo
                      id="takesAcidReducer"
                      value={questionnaire.takesAcidReducer}
                      onChange={(v) => handleQuestionnaireChange("takesAcidReducer", v)}
                      label={t('dashboard.acidReducer')}
                    />
                    {(questionnaire.takesCalcium === "yes" ||
                      questionnaire.takesIron === "yes" ||
                      questionnaire.takesAcidReducer === "yes") && (
                      <div className="pl-3 border-l-2 rounded-r-lg py-2" style={{ borderColor: "#ffc107", background: "rgba(255,193,7,0.05)" }}>
                        <MobileYesNo
                          id="levoTwoHoursApart"
                          value={questionnaire.levoTwoHoursApart}
                          onChange={(v) => handleQuestionnaireChange("levoTwoHoursApart", v)}
                          label={t('dashboard.twoHoursApart')}
                        />
                      </div>
                    )}
                  </div>
                )}

                <MobileYesNo
                  id="thiamazole"
                  value={questionnaire.thiamazole}
                  onChange={(v) => handleQuestionnaireChange("thiamazole", v)}
                  label={t('dashboard.thiamazole')}
                />

                <MobileYesNo
                  id="propylthiouracil"
                  value={questionnaire.propylthiouracil}
                  onChange={(v) => handleQuestionnaireChange("propylthiouracil", v)}
                  label={t('dashboard.propylthiouracil')}
                />
                {questionnaire.propylthiouracil === "yes" && (
                  <div className="pl-3 border-l-2 rounded-r-lg py-2 mb-2" style={{ borderColor: "#b9e2ff", background: "rgba(185,226,255,0.1)" }}>
                    <Label className="font-medium text-sm block mb-2" style={{ color: "#181c20" }}>{t('dashboard.propylthiouracilDosage')}</Label>
                    <Input
                      type="text"
                      placeholder="e.g., 50, 100"
                      value={questionnaire.propylthiouracilDosage}
                      onChange={(e) => handleQuestionnaireChange("propylthiouracilDosage", e.target.value)}
                      className="h-11 text-base rounded-xl w-40"
                      data-testid="mobile-propylthiouracil-dosage"
                    />
                  </div>
                )}

                <MobileYesNo
                  id="cordarone"
                  value={questionnaire.cordarone}
                  onChange={(v) => handleQuestionnaireChange("cordarone", v)}
                  label={t('dashboard.cordarone')}
                />
              </div>
            </div>

            {/* Iodine Exposure */}
            <div className="rounded-2xl overflow-hidden" style={{ background: "#fff", boxShadow: "0 4px 20px rgba(0,101,141,0.06)" }}>
              <div className="p-5">
                <h3 className="font-bold mb-3" style={{ color: "#00658D" }}>{t('dashboard.iodineExposure')}</h3>
                
                <MobileYesNo
                  id="ctContrast"
                  value={questionnaire.ctContrast}
                  onChange={(v) => handleQuestionnaireChange("ctContrast", v)}
                  label={t('dashboard.ctContrast')}
                />
                <MobileYesNo
                  id="iodinatedSalt"
                  value={questionnaire.iodinatedSalt}
                  onChange={(v) => handleQuestionnaireChange("iodinatedSalt", v)}
                  label={t('dashboard.iodinatedSalt')}
                />
                <MobileYesNo
                  id="iodineSkin"
                  value={questionnaire.iodineSkin}
                  onChange={(v) => handleQuestionnaireChange("iodineSkin", v)}
                  label={t('dashboard.iodineSkin')}
                />
                <MobileYesNo
                  id="iodineSpray"
                  value={questionnaire.iodineSpray}
                  onChange={(v) => handleQuestionnaireChange("iodineSpray", v)}
                  label={t('dashboard.iodineSpray')}
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Lab Results */}
        {step === 3 && (
          <div className="p-4">
            <div className="mb-6">
              <h1 className="text-xl font-bold mb-2" style={{ color: "#181c20" }}>{t('dashboard.labResultsTitle')}</h1>
              <p className="text-sm" style={{ color: "#3e4850" }}>{t('dashboard.labResultsDesc')}</p>
            </div>

            <div className="rounded-2xl mb-4 overflow-hidden" style={{ background: "#fff", boxShadow: "0 4px 20px rgba(0,101,141,0.06)" }}>
              <div className="p-5">
                {THYROID_TESTS.map((test) => (
                  <div key={test.key} className="py-5 last:border-0">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="font-medium text-sm flex items-center gap-2" style={{ color: "#181c20" }}>
                        {test.name}
                        {test.required && <span style={{ color: "#d32f2f" }}>*</span>}
                        <span className="font-normal text-xs" style={{ color: "#94a3b8" }}>({test.unit})</span>
                      </Label>
                      <button
                        type="button"
                        onClick={() => setExpandedTest(expandedTest === test.key ? null : test.key)}
                        style={{ color: "#bdc8d1" }}
                      >
                        <Info className="w-4 h-4" />
                      </button>
                    </div>
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={testValues[test.key] || ""}
                      onChange={(e) => handleTestChange(test.key, e.target.value)}
                      className="h-11 text-base rounded-xl transition-all focus:ring-2"
                      style={{ background: "#ebeef3", border: "1px solid rgba(189,200,209,0.15)" }}
                      placeholder="0.00"
                      data-testid={`mobile-test-${test.key}`}
                      onFocus={e => e.target.style.borderColor = "#00658D"}
                      onBlur={e => e.target.style.borderColor = "rgba(189,200,209,0.15)"}
                    />
                    {expandedTest === test.key && (
                      <p className="text-xs mt-2 p-2 rounded-lg" style={{ color: "#3e4850", background: "#f1f4f9" }}>
                        {t(`tests.${test.key}Desc`) || `Enter your ${test.name} value`}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Ultrasound */}
        {step === 4 && (
          <div className="p-4">
            <div className="mb-6">
              <h1 className="text-xl font-bold mb-2" style={{ color: "#181c20" }}>{t('dashboard.ultrasoundTitle')}</h1>
              <p className="text-sm" style={{ color: "#3e4850" }}>{t('dashboard.ultrasoundDesc')}</p>
            </div>

            <div className="rounded-2xl mb-4 overflow-hidden" style={{ background: "#fff", boxShadow: "0 4px 20px rgba(0,101,141,0.06)" }}>
              <div className="p-5 space-y-4">
                <div>
                  <label htmlFor="mobile-ultrasound-text" className="font-medium text-sm block mb-2" style={{ color: "#181c20" }}>
                    {t('dashboard.ultrasoundTextLabel')}
                  </label>
                  <textarea
                    id="mobile-ultrasound-text"
                    data-testid="mobile-input-ultrasound-text"
                    className="w-full min-h-[140px] p-3 rounded-xl focus:outline-none focus:ring-2 text-sm transition-all"
                    style={{ background: "#ebeef3", border: "1px solid rgba(189,200,209,0.15)" }}
                    placeholder={t('dashboard.ultrasoundTextPlaceholder')}
                    value={ultrasoundText}
                    onChange={(e) => setUltrasoundText(e.target.value)}
                    onFocus={e => e.target.style.borderColor = "#00658D"}
                    onBlur={e => e.target.style.borderColor = "rgba(189,200,209,0.15)"}
                  />
                  {extractedNotice && (
                    <p className="text-sm flex items-center gap-1 mt-1" style={{ color: "#00658D" }}>
                      <CheckCircle className="w-4 h-4" />
                      {t('dashboard.ultrasoundExtracted')}
                    </p>
                  )}
                </div>

                <div>
                  <label className="font-medium text-sm block mb-2" style={{ color: "#181c20" }}>
                    {t('dashboard.ultrasoundUploadLabel')}
                  </label>
                  {uploadedFile ? (
                    <div className="flex items-center gap-3 p-3 rounded-xl" style={{ background: "rgba(185,226,255,0.2)", border: "1px solid rgba(0,101,141,0.2)" }}>
                      <FileText className="w-5 h-5 flex-shrink-0" style={{ color: "#00658D" }} />
                      <span className="text-sm flex-1 truncate" style={{ color: "#00658D" }}>{uploadedFile.name}</span>
                      <button onClick={() => { setUploadedFile(null); setExtractedNotice(false); }} style={{ color: "#94a3b8" }}>
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => document.getElementById('mobile-ultrasound-file').click()}
                      disabled={ultrasoundUploading}
                      className="w-full p-4 border-2 border-dashed rounded-xl flex flex-col items-center gap-2 disabled:opacity-50"
                      style={{ borderColor: "#bdc8d1" }}
                      data-testid="mobile-upload-ultrasound-btn"
                    >
                      {ultrasoundUploading ? (
                        <div className="flex items-center gap-2" style={{ color: "#3e4850" }}>
                          <div className="animate-spin w-5 h-5 border-2 border-t-transparent rounded-full" style={{ borderColor: "#00658D", borderTopColor: "transparent" }} />
                          <span className="text-sm">{t('dashboard.ultrasoundUploading')}</span>
                        </div>
                      ) : (
                        <>
                          <Upload className="w-6 h-6" style={{ color: "#bdc8d1" }} />
                          <span className="text-sm" style={{ color: "#6e7881" }}>{t('dashboard.ultrasoundUploadHint')}</span>
                        </>
                      )}
                    </button>
                  )}
                  <input
                    id="mobile-ultrasound-file"
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg,.webp"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setUltrasoundUploading(true);
                      setExtractedNotice(false);
                      try {
                        const formData = new FormData();
                        formData.append("file", file);
                        const response = await axios.post(`${API}/upload/ultrasound`, formData, {
                          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
                        });
                        setUploadedFile({ name: file.name, id: response.data.file_id });
                        if (response.data.extracted_text) {
                          setUltrasoundText(response.data.extracted_text);
                          setExtractedNotice(true);
                        }
                      } catch (err) {
                        console.error("Upload failed:", err);
                      }
                      setUltrasoundUploading(false);
                      e.target.value = "";
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Price Info */}
            <div className="rounded-2xl overflow-hidden" style={{ background: "linear-gradient(135deg, rgba(185,226,255,0.3), rgba(0,101,141,0.05))", boxShadow: "0 4px 20px rgba(0,101,141,0.06)" }}>
              <div className="p-5 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: "rgba(0,101,141,0.1)" }}>
                  <CreditCard className="w-6 h-6" style={{ color: "#00658D" }} />
                </div>
                <div>
                  <p className="font-bold" style={{ color: "#181c20" }}>{t('dashboard.analysisFee')}</p>
                  <p className="text-sm" style={{ color: "#3e4850" }}>{t('payment.oneTimePayment')}: {"\u20ac"}20</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Fixed Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 backdrop-blur-xl z-50" style={{ background: "rgba(255,255,255,0.95)", boxShadow: "0 -4px 20px rgba(0,101,141,0.08)" }}>
        <div className="flex gap-3 p-4">
          {step > 1 && (
            <Button
              variant="outline"
              onClick={() => setStep(step - 1)}
              className="flex-1 h-12 rounded-full text-base border-0"
              style={{ background: "#ebeef3", color: "#3e4850" }}
              data-testid="mobile-back-btn"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              {t('common.back')}
            </Button>
          )}
          
          {step < 4 ? (
            <Button
              onClick={() => setStep(step + 1)}
              disabled={
                (step === 2 && !canProceedFromQuestionnaire()) ||
                (step === 3 && !canProceedFromLabResults())
              }
              className="flex-1 h-12 rounded-full text-base text-white"
              style={{ background: "linear-gradient(180deg, #00aeef 0%, #00658d 100%)", boxShadow: "0 4px 16px rgba(0,101,141,0.25)" }}
              data-testid="mobile-next-btn"
            >
              {t('common.next')}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          ) : step === 4 ? (
            <div className="flex gap-3 flex-1">
              {!ultrasoundText?.trim() ? (
                <Button
                  onClick={handleSubmit}
                  disabled={loading || !canProceedFromLabResults()}
                  className="flex-1 h-12 rounded-full text-base text-white"
                  style={{ background: "#3e4850" }}
                  data-testid="mobile-skip-ultrasound-btn"
                >
                  {loading ? "..." : t('dashboard.ultrasoundSkip')}
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex-1 h-12 rounded-full text-base text-white"
                  style={{ background: "linear-gradient(180deg, #00aeef 0%, #00658d 100%)", boxShadow: "0 4px 16px rgba(0,101,141,0.25)" }}
                  data-testid="mobile-submit-btn"
                >
                  {loading ? "..." : (
                    <>
                      {t('dashboard.proceedToPayment')}
                      <CreditCard className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
